import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Box,
  Select,
  Text,
  useToast,
} from "components-lib";
import { axiosInstance } from "config/api";
import { humanizeApiError } from "../../../utils/api_error_samples";

const defaultForm = {
  firstname: "",
  lastname: "",
  email: "",
  user_password: "",
  phoneno: "",
  whatsapp_no: "",
  clinic_id: "",
};

const createEmptyForm = () => ({ ...defaultForm });

const normalizeClinics = (data) => {
  if (!data) return [];

  const clinicsArray = Array.isArray(data)
    ? data
    : Array.isArray(data?.clinics)
      ? data.clinics
      : Array.isArray(data?.data)
        ? data.data
        : typeof data === "object"
          ? Object.values(data).filter((value) => value && typeof value === "object")
          : [];

  return clinicsArray
    .map((clinic) => ({
      id: clinic.id || clinic._id || clinic.clinic_id || clinic.clinicId,
      name: clinic.name || clinic.clinic_name || clinic.clinicName,
    }))
    .filter((clinic) => clinic.id && clinic.name);
};

const mapFrontdeskToForm = (frontdesk) => {
  if (!frontdesk) return createEmptyForm();

  const nameParts = (frontdesk.name || "").trim().split(" ");
  const defaultFirstname = frontdesk.firstname || nameParts[0] || "";
  const defaultLastname =
    frontdesk.lastname || (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");

  return {
    firstname: defaultFirstname,
    lastname: defaultLastname,
    email: frontdesk.email || "",
    user_password: "",
    phoneno: frontdesk.phone || "",
    whatsapp_no: frontdesk.whatsapp || "",
    clinic_id: frontdesk.clinicId || "",
  };
};

const FrontdeskEditModal = ({ isOpen, onClose, frontdesk, onFrontdeskUpdated }) => {
  const [form, setForm] = useState(createEmptyForm);
  const [errors, setErrors] = useState({});
  const [clinicOptions, setClinicOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const [updateResponse, setUpdateResponse] = useState(null);
  useEffect(() => {
    if (!isOpen) {
      setForm(createEmptyForm());
      setErrors({});
      return;
    }

    setForm(mapFrontdeskToForm(frontdesk));
    setErrors({});
  }, [frontdesk, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    axiosInstance
      .get("/api/clinic/allClinics", {
        validateStatus: (status) => status < 500,
      })
      .then((response) => {
        if (!isMounted) return;
        setClinicOptions(normalizeClinics(response.data));
      })
      .catch((error) => {
        console.error("Error fetching clinics for frontdesk edit:", error);
        if (!isMounted) return;
        setClinicOptions([]);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  // Auto-select clinic option when clinicOptions load and frontdesk contains clinic identifiers
  useEffect(() => {
    if (!isOpen || !frontdesk || clinicOptions.length === 0) return;

    // collect possible identifiers from frontdesk
    const candidates = new Set();
    const add = (v) => {
      if (v === undefined || v === null) return;
      candidates.add(String(v));
    };
    add(frontdesk.clinic_id);
    add(frontdesk.clinicId);
    add(frontdesk.clinic);
    add(frontdesk.clinic_name);
    add(frontdesk.clinicName);
    add(frontdesk.clinicId && frontdesk.clinicId.toString());
    add(frontdesk._id);
    add(frontdesk.id);

    // try to match by id first, then by name
    let matched = clinicOptions.find(
      (opt) => candidates.has(String(opt.id)) || candidates.has(String(opt.name)),
    );

    // If no match but form already has a clinic_id that doesn't exist in options, try to normalize it
    if (!matched && form?.clinic_id) {
      matched = clinicOptions.find((opt) => String(opt.id) === String(form.clinic_id));
    }

    if (matched) {
      setForm((prev) => ({ ...prev, clinic_id: String(matched.id) }));
    }
  }, [clinicOptions, frontdesk, isOpen]);

  const frontdeskId = useMemo(() => frontdesk?.id || "", [frontdesk]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.firstname.trim()) newErrors.firstname = "First name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    if (!form.phoneno.trim()) newErrors.phoneno = "Phone number is required";
    if (!form.clinic_id) newErrors.clinic_id = "Clinic is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!frontdeskId) return;

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        phoneno: form.phoneno.trim(),
        whatsapp_no: form.whatsapp_no.trim(),
        clinic_id: form.clinic_id ? Number(form.clinic_id) : undefined,
      };

      if (form.user_password.trim()) {
        payload.user_password = form.user_password.trim();
      }

        const response=   await axiosInstance.put(`/api/frontdesk/${frontdeskId}`, payload);
        setUpdateResponse(response)
      toast({
        title: "Frontdesk updated",
        description: "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      if (typeof onFrontdeskUpdated === "function") {
        onFrontdeskUpdated();
      }

      onClose();
    } catch (error) {
      console.error("Frontdesk update error:", error);
      const serverMessage = updateResponse?.data?.error || error.response?.data?.error || error.response?.data?.message || error.message;
      const { message: humanMessage, field } = humanizeApiError('/api/frontdesk', serverMessage);

      if (field) {
        setErrors(prev => ({ ...prev, [field]: humanMessage }));
      }

      toast({
        title: "Update failed",
        description: humanMessage || error.response?.data?.message || "We could not save the frontdesk details. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxW="620px">
        <ModalHeader>Edit Frontdesk</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <Flex direction="column" gap={4}>
              <FormControl isRequired isInvalid={!!errors.clinic_id}>
                <FormLabel>Clinic</FormLabel>
                <Select
                  placeholder="Select Clinic"
                  name="clinic_id"
                  value={form.clinic_id}
                  onChange={handleChange}
                >
                  {clinicOptions.map((clinic) => (
                    <option key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </option>
                  ))}
                </Select>
                {errors.clinic_id && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.clinic_id}
                  </Text>
                )}
              </FormControl>

              <Flex gap={3} direction={{ base: "column", md: "row" }}>
                <FormControl isRequired isInvalid={!!errors.firstname}>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    name="firstname"
                    value={form.firstname}
                    onChange={handleChange}
                  />
                  {errors.firstname && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.firstname}
                    </Text>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    name="lastname"
                    value={form.lastname}
                    onChange={handleChange}
                  />
                </FormControl>
              </Flex>

              <Flex gap={3} direction={{ base: "column", md: "row" }}>
                <FormControl isRequired isInvalid={!!errors.email}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.email}
                    </Text>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>New Password</FormLabel>
                  <Input
                    type="password"
                    name="user_password"
                    value={form.user_password}
                    onChange={handleChange}
                  />
                </FormControl>
              </Flex>

              <Flex gap={3} direction={{ base: "column", md: "row" }}>
                <FormControl isRequired isInvalid={!!errors.phoneno}>
                  <FormLabel>Phone Number</FormLabel>
                  <Input
                    name="phoneno"
                    value={form.phoneno}
                    onChange={handleChange}
                  />
                  {errors.phoneno && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.phoneno}
                    </Text>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>WhatsApp Number</FormLabel>
                  <Input
                    name="whatsapp_no"
                    value={form.whatsapp_no}
                    onChange={handleChange}
                  />
                </FormControl>
              </Flex>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting}
              isDisabled={!frontdeskId}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default FrontdeskEditModal;
